import { TextFieldProps } from "@mui/material";
import { ReactNode, createElement, forwardRef } from "react";

import MDInputRoot from "./MDInputRoot";

type MDInputProps = TextFieldProps & {
  error?: boolean;
  success?: boolean;
  disabled?: boolean;
  disabledIcon?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
};

const MDInput = forwardRef<HTMLDivElement, MDInputProps>((props, ref) => {
  const {
    error = false,
    success = false,
    disabled = false,
    disabledIcon = false,
    startAdornment,
    endAdornment,
    InputProps,
    slotProps,
    inputProps: rawInputProps,
    ...rest
  } = props;

  // Lift adornments from wrong places (root props, slotProps.input, inputProps) into InputProps
  const slotInput = slotProps?.input as Record<string, unknown> | undefined;
  const liftedStartAdornment =
    (InputProps as { startAdornment?: ReactNode } | undefined)?.startAdornment ??
    startAdornment ??
    (slotInput && (slotInput as { startAdornment?: ReactNode }).startAdornment) ??
    (rawInputProps && (rawInputProps as { startAdornment?: ReactNode }).startAdornment);

  const liftedEndAdornment =
    (InputProps as { endAdornment?: ReactNode } | undefined)?.endAdornment ??
    endAdornment ??
    (slotInput && (slotInput as { endAdornment?: ReactNode }).endAdornment) ??
    (rawInputProps && (rawInputProps as { endAdornment?: ReactNode }).endAdornment);

  const normalizedInputProps = {
    ...(InputProps || {}),
    ...(liftedStartAdornment !== undefined ? { startAdornment: liftedStartAdornment } : {}),
    ...(liftedEndAdornment !== undefined ? { endAdornment: liftedEndAdornment } : {}),
  } as MDInputProps["InputProps"];

  // Generic cleaner to omit adornments from records
  const omitAdornments = <T extends Record<string, unknown>>(obj?: T): T | undefined => {
    if (!obj) return obj;
    const clone: Record<string, unknown> = { ...obj };
    delete clone.startAdornment;
    delete clone.endAdornment;
    return clone as T;
  };

  // Clean slotProps.input to avoid leaking adornments to DOM
  const cleanedSlotProps = slotProps
    ? ({
        ...slotProps,
        ...(slotInput ? { input: omitAdornments(slotInput) } : {}),
      } as typeof slotProps)
    : slotProps;

  // Clean inputProps to avoid leaking adornments to native input element
  const cleanedInputProps = omitAdornments(
    rawInputProps as unknown as Record<string, unknown> | undefined
  ) as typeof rawInputProps;

  type OwnerState = { error: boolean; success: boolean; disabled: boolean; disabledIcon: boolean };
  type RootProps = TextFieldProps & { ownerState: OwnerState };

  return createElement(MDInputRoot as unknown as React.ComponentType<RootProps>, {
    ...(rest as TextFieldProps),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: ref as any,
    ownerState: { error, success, disabled, disabledIcon },
    InputProps: normalizedInputProps,
    slotProps: cleanedSlotProps,
    inputProps: cleanedInputProps,
  });
});

MDInput.displayName = "MDInput";
export default MDInput;
